import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map } from 'rxjs';
import { ToastHostComponent } from '@lite-workspace/shared/helm-ui/toast';
import { ClientOfflineBannerComponent, RouteVeilComponent } from '@lite-workspace/shared/helm-ui/app-root';
import { HLM_APP_SHELL_HEADER_H_CLASS } from '@lite-workspace/shared/helm-ui/app-shell';
import { IS_MOBILE } from '@lite-workspace/utils';
import { PreviewBannerComponent } from './ui/preview-banner.component';
import { armPreviewBfcacheGuard, exitClientPreview, isClientPreview, previewReturnFromUrl } from './core/client-session';
import { ClientStore } from './features/client/domain/+state/client.store';

@Component({
  selector: 'app-root',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, ToastHostComponent, PreviewBannerComponent, ClientOfflineBannerComponent, RouteVeilComponent],
  host: {
    // Toasts sit UNDER the app header. Its height lives on the hlm-app-shell host, which is INSIDE the router
    // outlet — a sibling toast host never inherits it. So the root republishes the shell's own class, for every
    // route: full-screen wizards keep the same inset and their own mobile bar is h-10.5 too.
    class: HLM_APP_SHELL_HEADER_H_CLASS,
  },
  // The ONE toast mount — full-screen routes (onboarding, wizards) live outside the shell and must toast too.
  template: `
    @if (previewBannerShown()) {
      <!-- Полоса предпросмотра стоит в потоке страницы над всеми экранами вкладки — она принадлежит не
           экрану, а самой вкладке, и живёт на каждом маршруте ученика, включая редактор альбома.
           Обычный поток, без sticky: app-root высотой в экран, и закреплённая полоса отлипала бы за
           первым же экраном прокрутки, оставив щель под шапкой ученика. Экраны в потоке (редактор,
           кабинет) она ужимает сама — они флекс-дети app-root; прокручиваемые экраны провожают её
           наверх, и их шапка встаёт к самому верху, как и стояла. -->
      <app-preview-banner text="Вы смотрите альбом глазами ученика" exitLabel="Выйти" (exit)="exitPreview()" />
    }
    <hlm-client-offline-banner [routes]="clientRoutes" [caption]="offlineCaption()" />
    <router-outlet />
    <hlm-toast-host position="top" [phone]="isMobile()" />
    <hlm-route-veil />
  `,
})
export class AppComponent {
  private readonly router = inject(Router);
  private readonly clientStore = inject(ClientStore);
  protected readonly isMobile = inject(IS_MOBILE);

  // Экраны ученика: галерея, визард, редактор и превью — превью открывается своим адресом, но это тот же редактор.
  protected readonly clientRoutes = /^\/(client|editor|preview)\//;

  // Адрес после каждой навигации.
  private readonly url = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.router.url),
    ),
    { initialValue: this.router.url },
  );

  // Запись предпросмотра живёт во вкладке, а не в сторе: пересчитываем её на каждой навигации —
  // вкладка входит в предпросмотр и выходит из него именно переходом.
  protected readonly previewBannerShown = computed(() => {
    const url = this.url();
    // До выбора персоны записи ещё нет, а полоса нужна уже на экране входа: там предпросмотр виден
    // по самому адресу — `?return=` к ученической ссылке приписывает только «Посмотреть как клиент».
    // Одного адреса мало: ученик, которому переслали ссылку с хвостом, видел бы чужую полосу и
    // «Выйти» в кабинет, куда ему нельзя. Полосу открывает только вердикт прав владельца.
    return (
      isClientPreview() || (this.clientStore.ownerPreview() && url.startsWith('/client/') && previewReturnFromUrl(url) !== null)
    );
  });

  constructor() {
    armPreviewBfcacheGuard();
  }

  protected exitPreview(): void {
    exitClientPreview();
  }

  // Вторая строка баннера принадлежит адресу, а не экрану. Экран, который её ставил, гасил её на уходе —
  // но редактор остаётся в памяти (маршрут переиспользуется), его onDestroy не наступает, и подпись про
  // несохранённые правки переезжала на следующий экран. Адрес такого хвоста не оставляет.
  protected readonly offlineCaption = computed(() => {
    const url = this.url();
    if (/^\/(editor|preview)\//.test(url)) return 'Без связи правки не сохранятся — подождите интернет';
    if (/^\/client\/[^/]+\/select\b/.test(url)) return 'Кнопка заработает, когда связь вернётся';
    return '';
  });
}
